import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { MessageService } from 'primeng/api';
import { filter } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PwaUpdateService {

  constructor(
    private swUpdate: SwUpdate,
    private messageService: MessageService
  ) {}

  /**
   * Initialize PWA update checking
   * Should be called in AppComponent ngOnInit
   */
  public init(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('Service Worker is not enabled');
      return;
    }

    // Check for available updates
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(evt => {
        console.log('New version available:', evt.latestVersion);
        this.promptUserToUpdate();
      });

    // Check for unrecoverable state
    this.swUpdate.unrecoverable.subscribe(event => {
      console.error('Service Worker in unrecoverable state:', event.reason);
      this.messageService.add({
        severity: 'error',
        summary: 'Błąd aplikacji',
        detail: 'Aplikacja wymaga przeładowania. Kliknij OK aby odświeżyć stronę.',
        sticky: true,
        closable: false
      });
      setTimeout(() => {
        document.location.reload();
      }, 3000);
    });

    // Periodically check for updates (every 6 hours)
    this.checkForUpdates();
    setInterval(() => {
      this.checkForUpdates();
    }, 6 * 60 * 60 * 1000);
  }

  /**
   * Manually check for updates
   */
  private async checkForUpdates(): Promise<void> {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    try {
      const updateAvailable = await this.swUpdate.checkForUpdate();
      if (updateAvailable) {
        console.log('Update check: new version available');
      } else {
        console.log('Update check: already on latest version');
      }
    } catch (error) {
      console.error('Failed to check for updates:', error);
    }
  }

  /**
   * Show notification to user about available update
   */
  private promptUserToUpdate(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Dostępna nowa wersja',
      detail: 'Nowa wersja aplikacji jest dostępna. Kliknij aby zaktualizować.',
      sticky: true,
      closable: true,
      data: {
        action: () => this.activateUpdate()
      }
    });

    // Auto-update after 30 seconds if user doesn't respond
    setTimeout(() => {
      this.activateUpdate();
    }, 30000);
  }

  /**
   * Activate the update and reload the page
   */
  private async activateUpdate(): Promise<void> {
    try {
      await this.swUpdate.activateUpdate();
      this.messageService.add({
        severity: 'success',
        summary: 'Aktualizacja',
        detail: 'Aplikacja zostanie przeładowana...',
        life: 2000
      });
      setTimeout(() => {
        document.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Failed to activate update:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Błąd aktualizacji',
        detail: 'Nie udało się zaktualizować aplikacji. Spróbuj odświeżyć stronę.',
        life: 5000
      });
    }
  }

  /**
   * Get current version info
   */
  public async getCurrentVersion(): Promise<any> {
    if (!this.swUpdate.isEnabled) {
      return null;
    }

    try {
      // Service worker doesn't expose version directly,
      // but we can get it from package.json or environment
      return { status: 'enabled' };
    } catch (error) {
      console.error('Failed to get version:', error);
      return null;
    }
  }
}
